import React, { useEffect, useState } from 'react';
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Card,
  InputGroup
} from 'react-bootstrap';
import { db } from '../FireBase';
import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import { Facebook, Instagram, Mail, Phone, User, FileText, MessageSquare, MapPin } from 'lucide-react';
import '../css/contact.css';
import MapLocation from '../components/MapComponent';

const ContactPage = () => {
  const [contactInfo, setContactInfo] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [status, setStatus] = useState({
    submitted: false,
    error: false,
    message: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { name, email, phone, subject, message } = formData;

    if (!name || !email || !phone || !subject || !message) {
      setStatus({
        submitted: true,
        error: true,
        message: 'Please fill in all fields.',
      });
      return;
    }

    try {
      await addDoc(collection(db, 'form-data'), {
        ...formData,
        type: 'contact-form',
      });

      setStatus({
        submitted: true,
        error: false,
        message: 'Your message has been sent successfully!',
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Error saving message:', error);
      setStatus({
        submitted: true,
        error: true,
        message: 'There was an error sending your message. Please try again.',
      });
    }
  };

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    const docRef = doc(db, 'settings', 'contact');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      setContactInfo(docSnap.data());
    }
  };

  return (
    <Container className="my-4">
      {/* Hero */}
      <section className="about-hero d-flex align-items-center justify-content-center" style={{ borderRadius: '1rem' }}>
        <div className="text-center text-white hero-text px-2">
          <h1 className="display-5 fw-bold">Get in Touch with Kabili Holidays</h1>
          <p className="lead">Plan your peaceful escape to Kodaikanal today.</p>
          <Button variant="light" size="lg" className="rounded-pill fw-bold" href="/rooms">Explore Stays</Button>
        </div>
      </section>

      <br />

      <Row style={{ marginBottom: '50px' }}>
        <Col md={6} className="mb-4">
          <Card className="shadow-sm rounded-4 h-100 p-3">
            <Card.Body>
              <Card.Title className="text-success mb-3">Reach Us</Card.Title>
              <p>If you have questions or want to book your stay, we’re just a message away!</p>
              <p><strong><Mail size={16} /> Email:</strong> <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a></p>
              <p><strong><Phone size={16} /> Phone:</strong> <a href={`tel:${contactInfo.phone}`}>{contactInfo.phone}</a></p>
              <p style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                <strong><MapPin size={16} /> Address:</strong><br />{contactInfo.address}
              </p>
              <div className="d-flex gap-3 mt-3">
                <a href={contactInfo.facebook} target="_blank" rel="noopener noreferrer">
                  <Facebook size={24} color="#3f7936" />
                </a>
                <a href={contactInfo.instagram} target="_blank" rel="noopener noreferrer">
                  <Instagram size={24} color="#3f7936" />
                </a>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-4">
          <Card className="shadow-sm rounded-4 h-100 p-3">
            <Card.Body>
              <Card.Title className="text-success mb-3">Send Us a Message</Card.Title>

              {status.submitted && (
                <Alert variant={status.error ? 'danger' : 'success'}>
                  {status.message}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><User size={16} /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><Mail size={16} /></InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><Phone size={16} /></InputGroup.Text>
                    <Form.Control
                      type="tel"
                      name="phone"
                      placeholder="Your Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Subject</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FileText size={16} /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="subject"
                      placeholder="Subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Message</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><MessageSquare size={16} /></InputGroup.Text>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="message"
                      placeholder="Write your message..."
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                    />
                  </InputGroup>
                </Form.Group>

                <div className="d-grid">
                  <Button type="submit" className='btn-own'>Send Message</Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <MapLocation address="Kabili Holidays, 7FCG+449, Attuvampatti, Tamil Nadu 624103"/>
      {/* Bottom CTA */}
      <Container className="cta-bottom text-white py-5 text-center mt-4">
        <h3 className="fw-bold mb-2">We’re Ready to Welcome You</h3>
        <p>Contact us now and start your journey to relaxation with Kabili Holidays.</p>
        <Button variant="light" size="lg" className="rounded-pill fw-bold mt-3" href="/rooms">Book a Stay</Button>
      </Container>
     
    </Container>
    
  );
};

export default ContactPage;
